import { useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useSelectedView } from 'reducers/viewsSlice';
import { getColumnDefinitionByType } from 'screens/performanceReview/manageAndInsights/TalentPlanning/columnDefinitions';
import { checkStaticDataCompensationEnabled } from 'utils/helpers/featureFlags';

import { additionalColumnsPropsMapStakeholderFlatList } from '../StakeHolderFlatListTable';
import { setModifiedStakeholdersRows, setOriginalStakeholdersRows } from '../slice/compensationDrawersSlice';
import { ColumnProps, ColumnDef } from './stakeholderTypes';
import {
  createAdditionalColumns,
  createMultiSelectMenuRendererFlatList,
  getDefaultStakeholderValues,
} from './stakeholderUtils';

export const useStakeholderTable = ({ columns, rows }: { columns: ColumnProps[]; rows: unknown[] }) => {
  const dispatch = useDispatch();
  const cohortDetails = useSelectedView();
  const [columnDefs, setColumnDefs] = useState<ColumnDef[]>([]);

  const MultiSelectMenuRenderer = useMemo(() => createMultiSelectMenuRendererFlatList(), []);

  const additionalColumns = useMemo(() => createAdditionalColumns(MultiSelectMenuRenderer), [MultiSelectMenuRenderer]);

  useEffect(() => {
    if (!columns) return;

    const newColumnDefs = columns.map((col: ColumnProps) => ({
      colId: col.columnId,
      field: col.columnId,
      id: col.columnId,
      displayName: col.displayName,
      description: col.description,
      headerName: col.displayName,
      minWidth: 220,
      ...getColumnDefinitionByType(col),
      ...(additionalColumnsPropsMapStakeholderFlatList[col.columnType] || {}),
    }));

    newColumnDefs.push(...additionalColumns);

    setColumnDefs(newColumnDefs);
  }, [columns, additionalColumns]);

  useEffect(() => {
    if (rows.length > 0 && checkStaticDataCompensationEnabled()) {
      const newRows = getDefaultStakeholderValues(rows);
      dispatch(setOriginalStakeholdersRows(newRows));
      dispatch(setModifiedStakeholdersRows(newRows));
    }
  }, [dispatch, rows]);

  return { columnDefs, cohortDetails };
};
